import { Box, Stack, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/PersonOutlineRounded';
import ListItemSkeleton from './ListItemSkeleton';
import { PaymentWithTransfers } from '../../types/payments';
import { formatDollarAmount } from '../../utils/global/helper';
import TransactionBadge from './TransactionBadge';
import { getPaymentSplitsFromPayments } from '../../helpers/payments';

interface Props {
  payments: PaymentWithTransfers[];
  isLoading?: boolean;
}

function TransfersDetails({ payments, isLoading }: Props) {
  const theme = useTheme();
  // sort by updatedAt
  const transfers = payments
    .flatMap(payment => payment.transfers ?? [])
    .sort(
      (a, b) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
    );

  const paymentsSplit = getPaymentSplitsFromPayments(payments);

  return (
    <Stack spacing={0} width="100%" gap={2}>
      {!isLoading && (
        <>
          {paymentsSplit.map(payment => (
            <Box
              key={`${payment.id}-${payment.type}`}
              display="flex"
              justifyContent="space-between">
              <Typography variant="body2" color={theme.palette.medGrey.main}>
                {formatDollarAmount(payment.amount)} {payment.type}
              </Typography>
              <Typography variant="body2" color="black">
                {formatDollarAmount(payment.transfersAmount)} net of fees
              </Typography>
            </Box>
          ))}
          {transfers.map(transfer => {
            const transferDate = new Date(transfer.updatedAt);
            const Icon =
              transfer.entity === 'EMPLOYEE' ? PersonIcon : StorefrontIcon;
            return (
              <Box
                key={transfer.id}
                display="flex"
                gap={0.75}
                alignItems="center"
                justifyContent="space-between">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={0.75}
                  borderRadius="5px"
                  bgcolor={theme.palette.lightGrey.main}
                  sx={{
                    aspectRatio: '1/1',
                  }}>
                  <Icon width={22} height={22} />
                </Box>

                <Stack flex={1}>
                  <Typography variant="h5">
                    {formatDollarAmount(transfer.amount / 100)} sent to{' '}
                    {transfer.entity === 'EMPLOYEE' ? 'artist' : 'shop'}
                  </Typography>
                  <Typography variant="body2">
                    {format(transferDate, 'MMM d')} at{' '}
                    {format(transferDate, 'h:mm a')}{' '}
                  </Typography>
                </Stack>

                <TransactionBadge
                  light={false}
                  status={transfer.status}
                  tooltipText={
                    transfer.status === 'CREATED'
                      ? 'Transfer has been sent.'
                      : transfer.status === 'REVERSED'
                      ? 'Transfer has been reversed.'
                      : transfer.status === 'CANCELED'
                      ? 'Transfer has been canceled.'
                      : 'Transfer is pending until payments account is set up.'
                  }
                />
              </Box>
            );
          })}
          {!transfers?.length && (
            <Typography variant="body2">
              Transfer details are not available
            </Typography>
          )}
        </>
      )}
      {isLoading && <ListItemSkeleton />}
    </Stack>
  );
}

export default TransfersDetails;
